import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * MI-139 rev 5 — the page's index of the movement rows currently on screen.
 *
 * The grouped view disables the flat `search_movements` query on purpose, but
 * `movementsList` was doubling as the only place the page could look a loaded
 * movement up: the merge/group/split modals filter it, `patchMovements`
 * patches it, the detail panel reads its initial row out of it. With it empty
 * and the rows sitting in `Group`-local state, all three quietly did nothing.
 *
 * So rows live here instead, keyed by id, owned by the page. A group still
 * owns the *order* of its window; the objects are looked up through this.
 *
 * Deliberately a ref plus a version counter rather than plain state:
 * registering a window is not a visual change — the group that fetched it is
 * re-rendering anyway — and bumping page-level state on every prefetch would
 * re-render the whole list a dozen times on open. A patch *is* a visual
 * change, so that one bumps.
 */
export default function useMovementRowStore() {
  const byId = useRef(new Map());
  const [version, setVersion] = useState(0);

  /** Record a fetched window. Silent: no re-render. */
  const register = useCallback((movements) => {
    if (!Array.isArray(movements) || movements.length === 0) return;
    movements.forEach(m => { if (m?.id != null) byId.current.set(m.id, m); });
  }, []);

  /** Apply a partial update to rows already on screen, and re-render. */
  const patch = useCallback((ids, changes) => {
    let touched = false;
    ids.forEach(id => {
      const current = byId.current.get(id);
      if (!current) return;
      byId.current.set(id, { ...current, ...changes });
      touched = true;
    });
    if (touched) setVersion(v => v + 1);
  }, []);

  /** Replace one row wholesale — what the detail panel hands back on save. */
  const replace = useCallback((movement) => {
    if (movement?.id == null) return;
    byId.current.set(movement.id, movement);
    setVersion(v => v + 1);
  }, []);

  const get = useCallback((id) => byId.current.get(id) || null, []);

  /**
   * Swap a group's window for the current objects. Called at render time, so
   * a patch reaches every group showing that movement without any of them
   * having to know the edit happened.
   */
  const resolve = useCallback(
    (movements) => movements.map(m => byId.current.get(m.id) || m),
    []
  );

  /** Rows are a view of what is on screen, not a cache to keep warm: the
   *  filters or the grouping changing makes every one of them suspect. */
  const clear = useCallback(() => {
    byId.current = new Map();
    setVersion(v => v + 1);
  }, []);

  return useMemo(
    () => ({ register, patch, replace, get, resolve, clear, version }),
    [register, patch, replace, get, resolve, clear, version]
  );
}
